import { z } from "zod";
import { ActionFunction, LoaderFunction, redirect } from "react-router-dom";
import { apiClient, apiClientHooks } from "../bootstrapping/InitApiClient";
import { queryClient } from "../bootstrapping/InitReactQuery";
import { QueryClient } from "@tanstack/react-query";
import { unflattenObject } from "../utils/forms";
import { convertLocalFormSchemaToApiFormSchema, convertLocalFormSchemaToUpdateHeaders, zLocalFormSchema } from "../logic/applicationUpdateSchema";

const zAfterNames = z.enum(['homepage', 'identity', 'names', 'birth', 'addresses', 'documents', 'submit'])
type AfterNames = z.input<typeof zAfterNames>

export const after: Record<AfterNames, (props: { application_id: string }) => string> = {
  homepage: () => '/',
  identity: ({ application_id }) => `/applications/${application_id}/identity`,
  names: ({ application_id }) => `/applications/${application_id}/names`,
  birth: ({ application_id }) => `/applications/${application_id}/birth`,
  addresses: ({ application_id }) => `/applications/${application_id}/addresses`,
  documents: ({ application_id }) => `/applications/${application_id}/documents`,
  submit: ({ application_id }) => `/applications/${application_id}/submit`,
} as const

export const afterQueryParamKey = 'after'

//////// TODO: expose Index.sections logic, so we can work out if it's complete ... 
// if the next section is incomplete, then go to that section ... otherwise go to the homepage
// TODO: Say where we're going next (NEXT: Names, NEXT: Birth, NEXT: Addresses, NEXT: Documents, NEXT: Submit, NEXT: Home)

/**
 * Get the `after` key from the URL query parameters
 * @param urlString A URL or URI string (relative or absolute) which we pick off the `afterQueryParamKey` query parameter
 * @returns One of the keys from the `after` object, or `undefined` if the key is not found
 */
const getAfterKeyFromURL = (urlString: string): AfterNames | undefined => {
  const url = new URL(urlString);
  const value = url.searchParams.get(afterQueryParamKey)
  const parsed = zAfterNames.safeParse(value)
  return parsed.success ? parsed.data : undefined;
}

/**
 * Build the action path for the form (used in the `action` attribute of the form).
 * We use this to pass information through, such as where to redirect to after the form is submitted.
 * The defaultAfterKey is used if there is no `after` query parameter in the URL.
 * If there's no default and no query parameter, it will default to 'homepage'.
 * @returns An object with the URL query string and the key from the `after` object (so you can label the button)
 */
export const useActionPath = (defaultAfterKey: keyof typeof after = 'homepage') => {  
  // const location = useLocation()
  const urlAfterKey = getAfterKeyFromURL(document.location.href)
  const afterKey = urlAfterKey ?? defaultAfterKey

  return {
    url: `?${afterQueryParamKey}=${afterKey}`,
    key: afterKey,
  }
}

const getRedirectPath = ({ request, params }: Parameters<ActionFunction>[0]) => {
  const { application_id } = z.object({ application_id: z.string() }).parse(params)

  const afterKey = getAfterKeyFromURL(request.url);
  const afterFn = after[afterKey ?? 'homepage']

  return afterFn({ application_id })
}

/**
 * Loads the EBulkApplication by ID and ensures it's in the ReactQuery cache before loading the page
 */
export const loader = (queryClient: QueryClient): LoaderFunction => async ({ params }) => {
  const { application_id } = z.object({ application_id: z.string() }).parse(params)
  const queryKey = [...apiClientHooks.getKeyByAlias('getEBulkApplicationByID'), { params: { id: application_id } }]
  return await queryClient.ensureQueryData({ queryKey, queryFn: () => apiClient.getEBulkApplicationByID({ params: { id: application_id } }) })
}

/**
 * Saves the EBulkApplication form data by ID
 */
export const action: ActionFunction = async ({ request, params, context }) => {
  const { application_id } = z.object({ application_id: z.string() }).parse(params)

  const formData = await request.formData()
  const formObject = Object.fromEntries(formData)
  const unflattenedFormObject = unflattenObject(formObject)

  // Convert boolean strings from the form to actual booleans for the schema
  if ((unflattenedFormObject as any).NonApplicationFields?.NoPassportOrDriverLicence === 'true') {
    (unflattenedFormObject as any).NonApplicationFields.NoPassportOrDriverLicence = true
  }
  if ((unflattenedFormObject as any).NonApplicationFields?.NoPassportOrDriverLicence === 'false') {
    (unflattenedFormObject as any).NonApplicationFields.NoPassportOrDriverLicence = false
  }

  console.log("ApplicationUpdate.action: ", { request, params, context, formObject, unflattenedFormObject })
  if (Array.isArray(unflattenedFormObject)) {
    if (unflattenedFormObject.length === 0) {
      console.info('No form data was submitted, skipping update.')
      return redirect(`/`)
    }
    throw new Error("Form data is not an object, or an empty array")
  }
  const parsedFormObject = zLocalFormSchema.parse(unflattenedFormObject)
  
  const foldedFormData = convertLocalFormSchemaToApiFormSchema(parsedFormObject)
  const updateHeaders = convertLocalFormSchemaToUpdateHeaders(parsedFormObject)

  await apiClient.updateMyEBulkApplicationByID(foldedFormData, { params: { id: application_id }, headers: updateHeaders })
  queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getMyEBulkApplications') }) // Don't wait for this to complete
  queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplicationByID') }) // Don't wait for this to complete

  // If I've updated the name, then update the profile
  if (parsedFormObject.Forename && parsedFormObject.PresentSurname) {
    // Check it's different
    console.log("Considering updating profile...")
    const currentProfile = await apiClient.getMyProfile()
    if (currentProfile.given_name !== parsedFormObject.Forename || currentProfile.family_name !== parsedFormObject.PresentSurname) {
      // Update the profile
      console.log("Update my profile")
      await apiClient.updateMyProfile({
        given_name: parsedFormObject.Forename,
        family_name: parsedFormObject.PresentSurname,
      })
      queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getMyProfile') }) // Don't wait for this to complete
    }
  }

  return redirect(getRedirectPath({ request, params, context }))
}
